import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { RefreshCw, FileText, RotateCcw } from 'lucide-react';
import { useProject } from '@/context/ProjectContext';
import { useEVM } from '@/context/EVMContext';
import { useTargetCost } from '@/context/TargetCostContext';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ExportButtons } from '@/components/Export/ExportButtons';
import { TargetCostExportButtons } from '@/components/Export/TargetCostExportButtons';
import { createTranscript } from '@/utils/transcript';
import type { ChatAnalysis } from '@/types/whatsapp';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface ToolActionsProps {
  toolType: string;
}

/**
 * EVM-specific actions component - only rendered within EVMProvider
 */
function EVMActions() {
  const { dispatch } = useEVM();

  return (
    <ClearButton
      onClick={() => dispatch({ type: 'CLEAR_DATA' })}
      tooltip="Clear EVM data"
    />
  );
}

/**
 * TargetCost-specific actions component - only rendered within TargetCostProvider
 */
function TargetCostActions() {
  const { state, dispatch } = useTargetCost();
  const { toast } = useToast();

  const handleReset = () => {
    dispatch({ type: 'CLEAR_DATA' });
    toast({
      title: 'Reset Complete',
      description: 'All data has been cleared',
    });
  };

  return (
    <div className="flex items-center gap-2">
      <AlertDialog>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </Button>
              </AlertDialogTrigger>
            </TooltipTrigger>
            <TooltipContent>Reset all target cost data</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Calculation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset? This will clear all entered data and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset}>Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <TargetCostExportButtons data={state.data} />
    </div>
  );
}

/**
 * CVR/Project-specific actions component
 */
function CVRActions() {
  const { state: projectState, dispatch: projectDispatch } = useProject();

  return (
    <div className="flex items-center gap-2">
      <ClearButton
        onClick={() => projectDispatch({ type: 'CLEAR_DATA' })}
        tooltip="Clear project data"
      />
      <ExportButtons data={projectState.data} />
    </div>
  );
}

/**
 * Generic clear button component
 */
function ClearButton({ onClick, tooltip }: { onClick: () => void; tooltip: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            onClick={onClick}
            size="sm"
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Clear Form
          </Button>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ToolActions({ toolType }: ToolActionsProps) {
  // Render the appropriate actions component based on tool type
  // Each component is self-contained and uses its own context
  switch (toolType) {
    case 'evm':
      return <EVMActions />;
    case 'target-cost':
      return <TargetCostActions />;
    case 'cvr':
      return <CVRActions />;
    default:
      return null;
  }
}

export function TranscriptButton() {
  const { toast } = useToast();
  const location = useLocation();
  const [analysis, setAnalysis] = useState<ChatAnalysis | null>(null);

  const handleCreateTranscript = async (analysis: ChatAnalysis) => {
    try {
      if (!analysis) {
        throw new Error('No analysis data available');
      }
      await createTranscript(analysis);
      toast({
        title: 'Success',
        description: 'Transcript created successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create transcript',
        variant: 'destructive',
      });
    }
  };

  // Get analysis data from URL state
  useEffect(() => {
    const state = location.state as { analysis?: ChatAnalysis };
    if (state?.analysis) {
      setAnalysis(state.analysis);
    }
  }, [location]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCreateTranscript(analysis)}
            disabled={!analysis}
            className="gap-2"
          >
            <FileText className="h-4 w-4" />
            Create Transcript
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {analysis ? 'Generate Word-compatible transcript' : 'Analyze chat first to create transcript'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
